import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { SchoolModel, SchoolYearType } from "model/SchoolModel";
import { useAuth } from "pages";
import { getFeatureFlags } from "utils/featureFlags";

import { api } from "~utils";

import { SchoolYearsView } from "./SchoolYearsView";
import {
  getAcademicYearPeriod,
  getAcademicYears,
  hasExistingSchoolYearData,
  mapSchoolYearsToAcademicPeriods,
} from "./utils";

type SchoolYearsProps = {
  school: SchoolModel;
};

export function SchoolYears(props: SchoolYearsProps) {
  const {
    school: { id, years, hours },
  } = props;

  /*** Hooks **/
  const queryClient = useQueryClient();
  const { userInfo } = useAuth();

  /*** Constants **/
  const { SCHOOL_YEARS_V2 = false } = getFeatureFlags(userInfo);
  const schoolYear = years?.at(0);
  const academicYears = getAcademicYears(DateTime.now());
  const academicYearPeriods = mapSchoolYearsToAcademicPeriods(years, academicYears);

  /*** State **/
  const [selectedTab, setSelectedTab] = useState<string>(academicYears[0]?.value || "");

  const currentSchoolYear = SCHOOL_YEARS_V2 ? academicYearPeriods[selectedTab] : schoolYear;

  const hasExistingSchoolYear = SCHOOL_YEARS_V2
    ? currentSchoolYear && hasExistingSchoolYearData(currentSchoolYear, selectedTab) // check if ID is UUID vs placeholder
    : currentSchoolYear?.start && currentSchoolYear.end;

  const { mutate: setSchoolYears } = useMutation({
    mutationKey: ["schools", id, "years", currentSchoolYear?.id],
    mutationFn: async (payload?: SchoolYearType) => {
      if (!payload) return;

      const { data } = await api({
        method: hasExistingSchoolYear ? "PUT" : "POST",
        url: `api/admin/schools/${id}/years${hasExistingSchoolYear ? `/${currentSchoolYear?.id}` : ""}`,
        data: payload,
      });
      return data;
    },
    onSuccess: (data, payload) => {
      queryClient.setQueryData(["schools", id], (oldData: SchoolModel | undefined) => {
        return updateSchoolYearsInCache(oldData, payload, data);
      });
    },
  });

  /*** Render **/
  return (
    <SchoolYearsView
      hours={hours}
      schoolYear={currentSchoolYear}
      setSchoolYear={setSchoolYears}
      isTabbedView={SCHOOL_YEARS_V2}
      tabs={academicYears}
      tabsRangeData={academicYearPeriods}
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
    />
  );
}

/**
 * Updates the school years cache with a new or updated school year.
 * This function is used as the onSuccess handler for school year mutations to maintain
 * cache consistency without requiring a full refetch.
 *
 * @param oldData - Current SchoolModel instance from the query cache, or undefined
 * @param payload - School year data that was sent to the server, or undefined
 * @param responseData - Response data from the server containing the new/updated ID
 * @returns Updated SchoolModel instance with the new school year, or undefined if inputs are invalid
 *
 * @example
 * ```ts
 * const oldData = new SchoolModel({ "school/id": "123", years: [] });
 * const payload = { id: "temp-id", start: "2024-08-15T08:00:00.000Z", end: "2025-06-15T15:00:00.000Z" };
 * const responseData = { id: "uuid-456" };
 * const updated = updateSchoolYearsInCache(oldData, payload, responseData);
 * // Returns: SchoolModel with years: [{ id: "uuid-456", start: "2024-08-15...", end: "2025-06-15..." }]
 * ```
 */
export function updateSchoolYearsInCache(
  oldData: SchoolModel | undefined,
  payload: SchoolYearType | undefined,
  responseData: { id?: string } | undefined,
): SchoolModel | undefined {
  if (!oldData || !payload) return oldData;

  const updatedYear = { ...payload, id: responseData?.id || payload.id };
  const academicPeriod = getAcademicYearPeriod(updatedYear);
  const filteredYears = (oldData.years || []).filter(
    (year: SchoolYearType) => getAcademicYearPeriod(year) !== academicPeriod,
  );

  return {
    ...oldData,
    years: [...filteredYears, updatedYear],
  } as SchoolModel;
}
